/* [ASB pdp] Full-screen product photo zoom (tap to zoom, previous/next)
   Custom file — not part of Dawn 15.4.0.
   Built by: Codex (2026-10)
   What it does: replaces Dawn's media modal behaviour for image-only products; hooked from assets/product-modal.js.
   Settings/data: locales asb_zoom.*.
   Used by: sections/main-product.liquid, snippets/product-media-modal.liquid (data-asb-zoom). */
window.ASBProductZoom = class {
  constructor(modal) {
    this.modal = modal;
    this.stage = modal.querySelector('[role="document"]');
    this.canvas = modal.querySelector('.asb-zoom-canvas');
    this.toggle = modal.querySelector('[data-zoom-toggle]');
    this.previous = modal.querySelector('[data-zoom-prev]');
    this.next = modal.querySelector('[data-zoom-next]');
    this.hint = modal.querySelector('[data-zoom-hint]');
    this.message = modal.querySelector('[data-zoom-message]');
    this.toggle.addEventListener('click', () => this.zoom());
    this.previous.addEventListener('click', () => this.select(this.index - 1));
    this.next.addEventListener('click', () => this.select(this.index + 1));
    modal.addEventListener('keydown', event => {
      if (event.key === 'Tab') {
        // Recompute after every navigation/load: enabled controls change dynamically.
        const controls = getFocusableElements(modal).filter(element => element.getClientRects().length && !element.disabled);
        const index = controls.indexOf(document.activeElement);
        const next = (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
        event.preventDefault();
        event.stopPropagation();
        controls[next]?.focus();
        return;
      }
      if (!this.zoomed && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        this.select(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
      }
    });
    this.stage.addEventListener('click', event => {
      if (event.target === this.active && !this.dragged) this.zoom();
      this.dragged = false;
    });
    this.stage.addEventListener('dragstart', event => event.preventDefault());
    this.stage.addEventListener('pointerdown', event => {
      this.dragged = false;
      if (!this.zoomed || event.pointerType !== 'mouse' || event.button !== 0) return;
      this.drag = { x: event.clientX, y: event.clientY, left: this.stage.scrollLeft, top: this.stage.scrollTop };
      this.stage.setPointerCapture(event.pointerId);
    });
    this.stage.addEventListener('pointermove', event => {
      if (!this.drag) return;
      const x = event.clientX - this.drag.x, y = event.clientY - this.drag.y;
      if (Math.abs(x) + Math.abs(y) > 5) {
        this.dragged = true;
        this.modal.setAttribute('data-dragging', '');
        this.stage.scrollLeft = this.drag.left - x;
        this.stage.scrollTop = this.drag.top - y;
      }
    });
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      this.stage.addEventListener(type, () => { this.drag = null; this.modal.removeAttribute('data-dragging'); });
    }
    this.resize = new ResizeObserver(() => { if (modal.hasAttribute('open')) this.layout(); });
  }
  open(opener) {
    this.images = [...this.canvas.querySelectorAll(':scope > img[data-media-id]')];
    this.resize.observe(this.stage);
    this.select(Math.max(0, this.images.findIndex(image => image.dataset.mediaId === opener.dataset.mediaId)));
    removeTrapFocus();
    this.modal.querySelector('[id^="ModalClose-"]').focus();
  }
  close() {
    this.resize.disconnect();
    this.active?.removeEventListener('load', this.onLoad);
    this.active?.removeEventListener('error', this.onError);
    this.drag = null;
  }
  select(index) {
    if (index < 0 || index >= this.images.length) return;
    this.active?.removeEventListener('load', this.onLoad);
    this.active?.removeEventListener('error', this.onError);
    this.index = index;
    this.active = this.images[index];
    this.images.forEach(image => image.classList.toggle('active', image === this.active));
    this.zoomed = false;
    this.previous.disabled = index === 0;
    this.next.disabled = index === this.images.length - 1;
    this.modal.querySelector('[data-zoom-count]').textContent = `${index + 1} / ${this.images.length}`;
    this.message.textContent = this.message.dataset.loading;
    this.toggle.disabled = true;
    this.onLoad = () => { this.message.textContent = ''; this.toggle.disabled = false; this.layout(); this.refreshFocus(); };
    this.onError = () => { this.message.textContent = this.message.dataset.error; this.toggle.disabled = true; this.refreshFocus(); };
    this.active.addEventListener('load', this.onLoad);
    this.active.addEventListener('error', this.onError);
    this.active.loading = 'eager';
    if (this.active.complete) this.active.naturalWidth ? this.onLoad() : this.onError();
    this.layout(true);
    this.refreshFocus();
  }
  refreshFocus() {
    if (!this.modal.hasAttribute('open')) return;
    const focused = document.activeElement;
    const target = this.modal.contains(focused) && !focused.disabled ? focused : this.modal.querySelector('[id^="ModalClose-"]');
    removeTrapFocus();
    if (document.activeElement !== target) target.focus();
  }
  zoom() {
    if (this.toggle.disabled) return;
    this.zoomed = !this.zoomed;
    this.layout(true);
  }
  layout(center = false) {
    if (!this.active) return;
    const width = Math.max(1, this.stage.clientWidth - 24), height = Math.max(1, this.stage.clientHeight - 24);
    const ratio = this.active.naturalWidth / this.active.naturalHeight || Number(this.active.width) / Number(this.active.height) || 1;
    const fitted = Math.min(width, height * ratio);
    const rendered = Math.round(fitted * (this.zoomed ? 2 : 1));
    this.active.style.width = `${rendered}px`;
    this.active.style.height = `${Math.round(rendered / ratio)}px`;
    this.active.sizes = `${rendered}px`;
    this.modal.toggleAttribute('data-zoomed', this.zoomed);
    this.toggle.textContent = this.zoomed ? this.toggle.dataset.fit : this.toggle.dataset.in;
    this.toggle.setAttribute('aria-pressed', String(this.zoomed));
    this.hint.textContent = this.zoomed ? this.hint.dataset.zoomed : this.hint.dataset.fit;
    if (center) {
      this.stage.scrollLeft = (this.stage.scrollWidth - this.stage.clientWidth) / 2;
      this.stage.scrollTop = (this.stage.scrollHeight - this.stage.clientHeight) / 2;
    }
  }
};
